import { lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import Popover from '@components/ui/Popover/Popover';
import BellIcon from '@assets/icons/BellIcon';
import { useNotifications, UNREAD_COUNT_QUERY_KEY } from '@/hooks/useNotifications';
import { getUnreadCount } from '@/services/notificationService';
import { useTranslation } from '@/hooks/useTranslation';
import styles from './NotificationBell.module.scss';

import type { NotificationDto } from '@/types/notification';

// ============================================
// HELPERS
// ============================================

type TFn = (key: string, fallback: string) => string;

/** Lazy-load react-markdown — keeps it out of the main bundle (~36 KB gzip) */
const Markdown = lazy(() => import('react-markdown'));

/** Only allow inline markdown elements — no headings, images, tables, code blocks */
const ALLOWED_ELEMENTS = ['p', 'strong', 'em', 'a', 'code', 'del'];

function formatRelativeTime(isoDate: string, t: TFn): string {
  const diffMs = Date.now() - new Date(isoDate).getTime();
  const minutes = Math.floor(diffMs / 60_000);
  const hours = Math.floor(diffMs / 3_600_000);
  const days = Math.floor(diffMs / 86_400_000);

  if (minutes < 1) return t('K_NOTIF_JUST_NOW', 'Just now');
  if (minutes < 60)
    return t('K_NOTIF_MINUTES_AGO', '{count} minutes ago').replace('{count}', String(minutes));
  if (hours < 24)
    return t('K_NOTIF_HOURS_AGO', '{count} hours ago').replace('{count}', String(hours));
  return t('K_NOTIF_DAYS_AGO', '{count} days ago').replace('{count}', String(days));
}

// ============================================
// DROPDOWN (lazy-mounted when popover opens)
// ============================================

interface NotificationDropdownProps {
  unreadCount: number;
}

function NotificationDropdown({ unreadCount }: NotificationDropdownProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const {
    notifications,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    isFetchNextPageError,
    markAsRead,
    markAllAsRead,
    isMarkingAllAsRead,
  } = useNotifications();

  // ── Infinite scroll: IntersectionObserver sentinel ──
  const listRef = useRef<HTMLDivElement | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    const root = listRef.current;
    if (!sentinel || !root || !hasNextPage) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry?.isIntersecting && hasNextPage && !isFetchingNextPage) {
          void fetchNextPage();
        }
      },
      { root, rootMargin: '100px' },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const handleItemClick = useCallback(
    (notification: NotificationDto) => {
      if (!notification.read_at) markAsRead(notification.id);
      const link = notification.target_link;
      if (!link) return;
      // `target_link` is server-supplied notification content — guard against
      // open-redirect (CWE-601). Absolute URLs are followed only when they
      // resolve to this origin or a trusted *.workpay.site / *.myworkpay.site
      // host; otherwise the link is ignored.
      if (/^https?:\/\//i.test(link)) {
        try {
          const url = new URL(link);
          const trusted =
            url.origin === window.location.origin ||
            /(^|\.)(my)?workpay\.site$/i.test(url.hostname);
          if (trusted) window.location.href = link;
        } catch {
          /* malformed URL — ignore */
        }
      } else if (link.startsWith('/') && !link.startsWith('//')) {
        // Root-relative in-app path (`//` excluded — protocol-relative URL).
        void navigate({ to: link });
      }
    },
    [markAsRead, navigate],
  );

  return (
    <div className={styles.dropdown}>
      <div className={styles.header}>
        <span className={styles.headerTitle}>{t('K_NOTIF_TITLE', 'Notifications')}</span>
        <button
          type="button"
          className={styles.markAllReadButton}
          onClick={() => markAllAsRead()}
          disabled={unreadCount === 0 || isMarkingAllAsRead}
        >
          {t('K_NOTIF_MARK_ALL_READ', 'Mark all as read')}
        </button>
      </div>

      {isLoading ? (
        <div className={styles.empty}>{t('K_NOTIF_LOADING', 'Loading\u2026')}</div>
      ) : notifications.length === 0 ? (
        <div className={styles.empty}>{t('K_NOTIF_EMPTY', 'No notifications')}</div>
      ) : (
        <div className={styles.list} ref={listRef}>
          {notifications.map((notification) => {
            const isUnread = !notification.read_at;
            return (
              <button
                type="button"
                key={notification.id}
                className={`${styles.item} ${isUnread ? styles.itemUnread : ''}`}
                onClick={() => handleItemClick(notification)}
              >
                {isUnread ? (
                  <div className={styles.unreadDot} />
                ) : (
                  <div className={styles.readDotPlaceholder} />
                )}
                <div className={styles.itemContent}>
                  <div className={styles.itemTitle} title={notification.title}>
                    {notification.title}
                  </div>
                  <div className={styles.itemBody}>
                    <Suspense fallback={notification.body}>
                      <Markdown allowedElements={ALLOWED_ELEMENTS} unwrapDisallowed>
                        {notification.body}
                      </Markdown>
                    </Suspense>
                  </div>
                  <div className={styles.itemTimestamp}>
                    {formatRelativeTime(notification.created_on, t)}
                  </div>
                </div>
              </button>
            );
          })}

          {/* Sentinel + load-more states (only when list is shown) */}
          {hasNextPage && !isFetchNextPageError && (
            <>
              <div ref={sentinelRef} aria-hidden="true" className={styles.sentinel} />
              {isFetchingNextPage && (
                <div className={styles.loadingMore} role="status" aria-live="polite">
                  {t('K_NOTIF_LOADING_MORE', 'Loading more\u2026')}
                </div>
              )}
            </>
          )}

          {isFetchNextPageError && (
            <div className={styles.loadMoreError} role="alert">
              <span>{t('K_NOTIF_LOAD_ERROR', "Couldn't load more.")}</span>
              <button
                type="button"
                className={styles.retryButton}
                onClick={() => void fetchNextPage()}
              >
                {t('K_NOTIF_RETRY', 'Retry')}
              </button>
            </div>
          )}

          {!hasNextPage && !isFetchNextPageError && notifications.length > 0 && (
            <div className={styles.endOfList}>
              {t('K_NOTIF_END_OF_LIST', "You're all caught up")}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================
// BELL (always mounted in AppShell)
// ============================================

export default function NotificationBell() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  // Unread count — lightweight query, always active.
  const { data: unreadCount = 0 } = useQuery({
    queryKey: UNREAD_COUNT_QUERY_KEY,
    queryFn: getUnreadCount,
    staleTime: Infinity,
  });

  const bellAriaLabel =
    unreadCount > 0
      ? t('K_NOTIF_BELL_WITH_COUNT', '{count} unread notifications').replace(
          '{count}',
          String(unreadCount),
        )
      : t('K_NOTIF_TITLE', 'Notifications');

  return (
    <Popover
      content={open ? <NotificationDropdown unreadCount={unreadCount} /> : <div />}
      placement="bottom-end"
      trigger="click"
      open={open}
      onOpenChange={setOpen}
      className={styles.popoverWrapper}
    >
      <button type="button" className={styles.bellButton} aria-label={bellAriaLabel}>
        <BellIcon />
        {unreadCount > 0 && (
          <span className={styles.badge}>{unreadCount > 99 ? '99+' : unreadCount}</span>
        )}
      </button>
    </Popover>
  );
}
